// @ts-check
"use strict";

import React, { useState, useTransition } from "react";
import { ChevronDown, Hammer, LoaderCircle } from "lucide-react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { cn } from "@/lib/utils";

/**
 * @typedef {Object} ChatBubbleToolsCollapsibleProps
 * @property {React.ReactNode} children - Tool call components to render
 * @property {number} toolCount - Number of tools used
 * @property {string} [className] - Additional CSS classes
 */

/**
 * Collapsible container for completed tool calls
 * @param {ChatBubbleToolsCollapsibleProps & React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root>} props
 */
export function ChatBubbleToolsCollapsible({
  children,
  toolCount,
  className,
  ...props
}) {
  if (!children) {
    return null;
  }

  // Defer mounting tool components until the first open to avoid constructing
  // potentially expensive tool result trees for every closed collapsible in
  // the chat history. Once mounted, keep them alive so Radix can animate close.
  const [hasOpened, setHasOpened] = useState(false)
  const [isPending, startTransition] = useTransition()

  const title = "Tools Used";

  return (
    <CollapsiblePrimitive.Root
      {...props}
      className={cn(
        "not-prose rounded-xl border border-border/50 bg-transparent text-card-foreground overflow-hidden",
        className
      )}
      defaultOpen={false}
      onOpenChange={(open) => {
        if(open) {
          startTransition(() => {
            setHasOpened(true)
          })
        } else {
          setHasOpened(false)
        }
        
        props.onOpenChange?.(open)
      }}
    >
      <CollapsiblePrimitive.Trigger className="group flex w-full items-center justify-between px-5 py-4 font-medium bg-transparent transition-all duration-200 border-b border-border/30">
        <div className="flex items-center gap-3">
          { !isPending && <Hammer className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" /> }
          { isPending && <LoaderCircle className="h-4 w-4 text-muted-foreground animate-spin" /> }

          <span className="text-sm font-semibold text-foreground/90">{title}</span>
          <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 text-xs font-semibold text-muted-foreground bg-transparent border border-muted-foreground/30 rounded-md transition-colors group-hover:border-foreground/50 group-hover:text-foreground">
            {toolCount || 0}
          </span>
        </div>
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-all duration-300 group-data-[state=open]:rotate-180 group-hover:text-foreground" />
      </CollapsiblePrimitive.Trigger>

      <CollapsiblePrimitive.Content className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
        <div className="flex flex-col px-4 py-3 gap-2">
          {hasOpened && children}
        </div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}

export default ChatBubbleToolsCollapsible;